import { Widget } from '../../core/Widget.js';
import { Loc } from '../../locales/registry.js';
import { ViewWidgetHelper } from '../../view/ViewWidgetHelper.js';
import { ViewCmdType } from '../../view/IViewWidget.js';
import { AsyncProcessPoller } from './ProcessPoller.js';

/**
 * Класс виджета для отображения асинхронных процессов
 */
class AsyncWidget extends Widget {
    constructor() {
        super(...arguments);
        this.lastReport = 0;
        this.lastCmd = 0;
        this.status = null;
        //#endregion
    }
    /** Ключевой класс для поиска виджета */
    static _cssKey() {
        return 'im-async';
    }
    // --- Конструктор и основная логика ---
    /**
     * Инициализация виджета
     */
    _create(op) {
        this.owner = op.owner;
        this.updateInterval = 2000;
        if (op.updateInterval && op.updateInterval > 1000) {
            this.updateInterval = op.updateInterval;
        }
        if (!this.updateInterval || this.updateInterval < 300) {
            this.updateInterval = 2000;
        }
        this.statusPosition = op.statusPosition ?? 'right';
        this.use_progress = true;
        this.use_report = true;
        switch (op.mode) {
            case 'min':
                this.use_progress = false;
                this.use_report = false;
                break;
            case 'simple':
            case 'progressonly':
                this.use_report = false;
                break;
        }
        this.process = op.proc ? op.proc : this.element.dataset.proc;
        this.title = document.createElement('label');
        this.title.innerHTML = op.initText ?? Loc.init;
        if (op.mode !== 'progressonly') {
            this.element.appendChild(this.title);
        }
        if (this.element.classList.contains('loading-circle')) {
            const loadingContainer = document.createElement('div');
            loadingContainer.className = 'loading-container simple on';
            loadingContainer.innerHTML = '<span class="loading ui-corner-all"></span>';
            this.element.prepend(loadingContainer);
        }
        if (this.use_progress) {
            this.progressLabel = document.createElement('p');
            this.progress = document.createElement('im-progress-bar');
            this.progress.className = 'async-progress';
            this.element.append(this.progressLabel);
            this.element.append(this.progress);
        }
        if (this.use_report) {
            this.report = document.createElement('div');
            this.report.className = 'async-report';
            this.element.appendChild(this.report);
        }
        if (op.contentHtml !== undefined) {
            this.element.insertAdjacentHTML('beforeend', op.contentHtml);
        }
        if (op.cbi && op.cbi > 0) {
            this.lastCmd = op.cbi;
        }
        // Инициализируем batch-поллер с тем же интервалом, что и раньше
        this.poller = new AsyncProcessPoller({
            element: this.element,
            owner: this.owner,
            intervalMs: this.updateInterval
        });
        // Пробросим обработчики — используем существующую логику отрисовки
        this.poller.setHandlers({
            onWorking: (_pid, payload) => {
                this.showFromPayload(payload);
            },
            onDone: (_pid, payload) => {
                this.showFromPayload(payload);
            },
            onFailed: (_pid, payload) => {
                this.showFromPayload(payload);
            },
            onDeleted: (_pid) => {
                //TODO: Продумать поведение
                if (this.title)
                    this.title.innerHTML = `<small>${Loc.notFound}</small>`;
                // Удаляем если не в диалоге
                if (!this.element.closest('im-dialog'))
                    this.element.remove();
            }
        });
        // Старт наблюдения по текущему процессу
        const pid = Number(this.process);
        if (!Number.isNaN(pid) && pid > 0) {
            this.poller.add(pid, this.lastCmd /*lastCmd*/, this.lastReport /*lastReport*/);
        }
    }
    /**
     * Отмена процесса
     */
    cancelProc() {
        const cmdOptions = { process: this.process };
        ViewWidgetHelper.ExecCmd(this.element, {
            cmdType: ViewCmdType.asyncprocess,
            cmd: 'cancel',
            options: cmdOptions,
            onError: () => console.error('err update process'),
            setBusy: false
        }, this.owner);
    }
    /**
     * Получение ID процесса
     * @returns ID процесса
     */
    procID() {
        return this.process;
    }
    /** Отрисовка из «родного» payload’а procStatus/procBatch.items[] */
    showFromPayload(payload) {
        // Заголовок
        if (this.title) {
            switch (this.statusPosition) {
                case 'left':
                    this.title.innerHTML = `<small>${payload.statusStr}</small> ${payload.title}`;
                    break;
                case 'none':
                    this.title.innerHTML = payload.title;
                    break;
                default:
                    this.title.innerHTML = `${payload.title} <small>${payload.statusStr}</small>`;
                    break;
            }
        }
        // Прогресс
        if (this.use_progress && this.progressLabel && this.progress) {
            this.progressLabel.innerHTML = payload.progressLabel || '';
            this.progress.valueProgress = payload.progress ?? 0;
        }
        // Репорт (оставляем твою логику склейки повторов)
        if (this.use_report && this.report && payload.report) {
            const lastChild = this.report.lastElementChild;
            const prevReport = lastChild?.innerHTML;
            if (prevReport && prevReport.length > 2) {
                let trimmed = prevReport;
                while (trimmed.length > 2 && trimmed.endsWith('+'))
                    trimmed = trimmed.slice(0, -1);
                if (`<p>${trimmed}</p>` === payload.report) {
                    if (lastChild)
                        lastChild.innerHTML = lastChild.innerHTML + '+';
                }
                else {
                    this.report.insertAdjacentHTML('beforeend', payload.report);
                }
            }
            else {
                this.report.insertAdjacentHTML('beforeend', payload.report);
            }
        }
        // Сохраняем маркеры и статус как раньше
        this.lastReport = payload.lastReport;
        this.lastCmd = payload.lastCmd;
        this.status = payload.status;
    }
    //#region Базовые методы и свойства жизненного цикла
    /**
     * Уничтожение экземпляра класса
     */
    _onDestroy() {
        this.poller?.destroy();
        this.poller = undefined;
        this.owner = undefined;
    }
}
/** Имя виджета */
AsyncWidget._alias = "Async";

export { AsyncWidget };
